import { useQueries } from '@tanstack/react-query';
import { useFamily } from 'src/clients/hooks/useFamily';
import type { MeetingScope } from 'src/meetings/scope';
import type { DynamicUiData, EntitiesByType, EntityWithMetadata } from 'src/UI/dynamicUI/types';

export type ProfessionalConnection = {
  id: string;
  name: string;
  type: string;
  email: string;
};

type Family = NonNullable<ReturnType<typeof useFamily>['family']>;

/**
 * Professional connections are `ProfessionalConnection` entities stored on each
 * client's non-financial data. For a family scope we gather them across every
 * family member; for an individual scope just that client.
 */
export function useProfessionalConnections(scope: MeetingScope): ProfessionalConnection[] {
  const familyId = scope.kind === 'family' ? scope.familyId : undefined;
  const { family } = useFamily(familyId);

  const clientIds = scope.kind === 'individual' ? [scope.clientId] : familyMemberClientIds(family);

  const results = useQueries({
    queries: clientIds.map(clientId => ({
      queryKey: ['clients', clientId, 'non-financial-data'],
      enabled: !!clientId,
    })),
  });

  const seen = new Set<string>();
  return results.flatMap(result =>
    toConnections((result.data as DynamicUiData | undefined)?.entities).filter(
      connection => !seen.has(connection.id) && (seen.add(connection.id), true),
    ),
  );
}

function familyMemberClientIds(family: Family | null): string[] {
  const seen = new Set<string>();
  return [
    family?.mainFamilyMember.clientId,
    ...(family?.secondaryFamilyMembers ?? []).map(member => member.clientId),
    ...(family?.members ?? []).map(member => member.clientId),
  ].filter((id): id is string => !!id && !seen.has(id) && (seen.add(id), true));
}

function toConnections(entities: EntitiesByType | undefined): ProfessionalConnection[] {
  const value = entities?.ProfessionalConnection;
  if (!value) return [];
  const list: EntityWithMetadata[] = Array.isArray(value) ? value : [value];
  return list
    .map(entity => {
      const fields = entity.entity?.fields ?? {};
      return {
        id: entity.id,
        name: String(fields.name ?? '').trim(),
        type: String(fields.type ?? '').trim(),
        email: String(fields.email ?? '').trim(),
      };
    })
    .filter(connection => connection.name.length > 0);
}
